import { defineComponent, h, onBeforeUnmount, onMounted, ref, watch } from 'vue'

/**
 * 桌面端扫码区的二维码。完全本地生成，不请求任何外部服务。
 *
 * - 编码用 uqr（MIT，零依赖）。只在桌面宽度（≥ 980px）才按需 import，手机端不下载这段代码；
 * - 渲染成单个 <path> 的内联 SVG，任意缩放都清晰；
 * - 内容是本页地址：只拼「同源 + 路径 + ?code=邀请码」，不把地址栏里其它参数（含 hash、
 *   不受信的查询参数）原样带进二维码。
 */
const DesktopQuery = '(min-width: 980px)'

export const QrCode = defineComponent({
  name: 'QrCode',
  props: {
    /** 邀请码；首页不传。 */
    code: { type: String, default: '' },
    label: { type: String, required: true },
  },
  setup(props) {
    const path = ref('')
    const size = ref(0)
    let media: MediaQueryList | null = null
    let disposed = false

    function pageUrl(): string {
      const base = `${window.location.origin}${window.location.pathname}`
      return props.code ? `${base}?code=${encodeURIComponent(props.code)}` : base
    }

    async function render(): Promise<void> {
      if (!media || !media.matches) return
      try {
        const { encode } = await import('uqr')
        if (disposed) return
        const result = encode(pageUrl(), { ecc: 'M', border: 0 })
        let d = ''
        result.data.forEach((row, y) => {
          row.forEach((dark, x) => {
            if (dark) d += `M${x} ${y}h1v1h-1z`
          })
        })
        size.value = result.size
        path.value = d
      } catch {
        // 生成失败就不显示二维码，扫码区由样式整体收起，不影响其它内容
        path.value = ''
      }
    }

    const onChange = () => void render()

    onMounted(() => {
      if (typeof window.matchMedia !== 'function') return
      media = window.matchMedia(DesktopQuery)
      if (typeof media.addEventListener === 'function') media.addEventListener('change', onChange)
      else if (typeof media.addListener === 'function') media.addListener(onChange)
      void render()
    })

    onBeforeUnmount(() => {
      disposed = true
      if (!media) return
      if (typeof media.removeEventListener === 'function') media.removeEventListener('change', onChange)
      else if (typeof media.removeListener === 'function') media.removeListener(onChange)
    })

    watch(() => props.code, () => void render())

    return () =>
      path.value
        ? h(
            'svg',
            {
              class: 'official-qr-code',
              xmlns: 'http://www.w3.org/2000/svg',
              viewBox: `-2 -2 ${size.value + 4} ${size.value + 4}`,
              role: 'img',
              'aria-label': props.label,
              'shape-rendering': 'crispEdges',
            },
            [h('rect', { x: -2, y: -2, width: size.value + 4, height: size.value + 4, fill: '#fff' }), h('path', { d: path.value, fill: '#101828' })],
          )
        : h('span', { class: 'official-qr-code official-qr-code-pending', 'aria-hidden': 'true' })
  },
})
